import { uniqBy } from 'lodash';

import { ClientProfileRelationshipType } from '@/types/schema';

export interface SidebarPerson {
  id: string;
  displayName: string;
  firstName: string;
  onboardedIndividualSuggestion?: { id: string }[] | null;
  relationships?:
    | {
        id: string;
        type: ClientProfileRelationshipType;
        fromClientProfile: {
          id: string;
        };
        toClientProfile: {
          id: string;
        };
      }[]
    | null;
}

export type FamilyRelationshipType =
  | ClientProfileRelationshipType.Spouse
  | ClientProfileRelationshipType.ExSpouse
  | ClientProfileRelationshipType.DomesticPartner
  | ClientProfileRelationshipType.ExDomesticPartner
  | ClientProfileRelationshipType.Parent
  | ClientProfileRelationshipType.StepParent
  | ClientProfileRelationshipType.Child
  | ClientProfileRelationshipType.StepChild
  | ClientProfileRelationshipType.Grandchild
  | ClientProfileRelationshipType.Grandparent
  | ClientProfileRelationshipType.Sibling
  | ClientProfileRelationshipType.UncleAunt
  | ClientProfileRelationshipType.NephewNiece
  | ClientProfileRelationshipType.Cousin
  | ClientProfileRelationshipType.OtherRelative;

export type FamilyRelationships = Record<
  FamilyRelationshipType,
  SidebarPerson[]
>;

export const ADJACENT_RELATIONSHIP_TYPES = [
  ClientProfileRelationshipType.Spouse,
  ClientProfileRelationshipType.ExSpouse,
  ClientProfileRelationshipType.DomesticPartner,
  ClientProfileRelationshipType.ExDomesticPartner,
  ClientProfileRelationshipType.Parent,
  ClientProfileRelationshipType.StepParent,
  ClientProfileRelationshipType.Child,
  ClientProfileRelationshipType.StepChild,
];

export const EMPTY_PERSON_RELATIONSHIPS: Readonly<FamilyRelationships> = {
  [ClientProfileRelationshipType.Spouse]: [],
  [ClientProfileRelationshipType.ExSpouse]: [],
  [ClientProfileRelationshipType.DomesticPartner]: [],
  [ClientProfileRelationshipType.ExDomesticPartner]: [],
  [ClientProfileRelationshipType.Parent]: [],
  [ClientProfileRelationshipType.StepParent]: [],
  [ClientProfileRelationshipType.Child]: [],
  [ClientProfileRelationshipType.StepChild]: [],
  [ClientProfileRelationshipType.Grandchild]: [],
  [ClientProfileRelationshipType.Grandparent]: [],
  [ClientProfileRelationshipType.Sibling]: [],
  [ClientProfileRelationshipType.UncleAunt]: [],
  [ClientProfileRelationshipType.NephewNiece]: [],
  [ClientProfileRelationshipType.Cousin]: [],
  [ClientProfileRelationshipType.OtherRelative]: [],
};

export const RELATIONSHIP_TYPE_DISPLAY_NAMES: Record<
  FamilyRelationshipType,
  string
> = {
  [ClientProfileRelationshipType.Spouse]: 'Spouse',
  [ClientProfileRelationshipType.ExSpouse]: 'Ex-spouse',
  [ClientProfileRelationshipType.DomesticPartner]: 'Partner',
  [ClientProfileRelationshipType.ExDomesticPartner]: 'Ex-partner',
  [ClientProfileRelationshipType.Parent]: 'Parent',
  [ClientProfileRelationshipType.StepParent]: 'Stepparent',
  [ClientProfileRelationshipType.Child]: 'Child',
  [ClientProfileRelationshipType.StepChild]: 'Stepchild',
  [ClientProfileRelationshipType.Grandchild]: 'Grandchild',
  [ClientProfileRelationshipType.Grandparent]: 'Grandparent',
  [ClientProfileRelationshipType.Sibling]: 'Sibling',
  [ClientProfileRelationshipType.UncleAunt]: 'Uncle/Aunt',
  [ClientProfileRelationshipType.NephewNiece]: 'Nephew/Niece',
  [ClientProfileRelationshipType.Cousin]: 'Cousin',
  [ClientProfileRelationshipType.OtherRelative]: 'Other Relative',
};

/**
 * Get all relationships for a person (spouses, parents, children)
 */
export function getPersonRelationships(
  personId: string,
  people: SidebarPerson[]
): FamilyRelationships {
  if (!people) {
    return EMPTY_PERSON_RELATIONSHIPS;
  }

  const allRelationships = people.flatMap((cp) => cp.relationships ?? []);

  const thisClientProfileRelationships = allRelationships.filter(
    (r) => r?.toClientProfile?.id === personId
  );

  const findForRelationship = (type: ClientProfileRelationshipType) => {
    return uniqBy(
      thisClientProfileRelationships
        .filter((r) => r?.type === type)
        .flatMap((r) => r?.fromClientProfile.id ?? []),
      (id) => id
    );
  };

  return (
    Object.keys(EMPTY_PERSON_RELATIONSHIPS) as FamilyRelationshipType[]
  ).reduce(
    (acc, type) => {
      acc[type] = people.filter((p) =>
        findForRelationship(type).includes(p.id)
      );
      return acc;
    },
    { ...EMPTY_PERSON_RELATIONSHIPS }
  );
}

/**
 * Find a person by their ID
 */
export function findPersonById(
  personId: string,
  people: SidebarPerson[]
): SidebarPerson | undefined {
  return people.find((person) => person.id === personId);
}
